import React, { useContext, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import useToggle from 'hooks/useToggle'
import IconButton from 'components/Luxkit/Button/IconButton'
import Dropdown from 'components/Luxkit/Dropdown/Dropdown'
import DropdownContainer from 'components/Luxkit/Dropdown/DropdownContainer'
import LineEllipsisVIcon from 'components/Luxkit/Icons/line/LineEllipsisVIcon'
import ListItem from 'components/Luxkit/List/ListItem'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import LineBarsIcon from 'components/Luxkit/Icons/line/LineBarsIcon'
import LineCommentExclamationIcon from 'components/Luxkit/Icons/line/LineCommentExclamationIcon'
import LinePhoneIcon from 'components/Luxkit/Icons/line/LinePhoneIcon'
import ContactContext from 'contexts/contactContext'
import LinePlusIcon from 'components/Luxkit/Icons/line/LinePlusIcon'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import ClickableLink from 'components/Common/Clickable/ClickableLink'

const HeaderContainer = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 2fr 1fr;
  grid-template-areas:
    "history header menu";
  height: ${rem(60)};
  padding: 0 ${rem(24)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};


  ${mediaQueryUp.desktop} {
    grid-template-columns: 1fr 1fr 1fr;
  }

  ${mediaQueryDown.mobile} {
    padding: 0;
  }
`

const StyledHistory = styled.div`
  grid-area: history;
  display: flex;

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const StyledHeader = styled.div`
  grid-area: header;
  display: flex;
  justify-content: center;
  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const StyledLELogo = styled(LuxuryEscapesSmallLogo)`
  width: ${rem(32)};
`

const StyledMenu = styled.div`
  grid-area: menu;
  display: flex;
  justify-content: right;
  position: relative;
`

interface Props {
  toggleHistory?: () => void;
  toggleFeedback?: () => void;
  onNewChat: () => void;
  disableReportFeedback?: boolean;
}

function Header({
  toggleHistory,
  toggleFeedback,
  onNewChat,
  disableReportFeedback,
}: Props) {
  const { value: open, toggle, off } = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const currentContact = useContext(ContactContext)
  const localContact = currentContact.local

  return (
    <HeaderContainer>
      <StyledHistory>
        <IconButton
          kind="tertiary"
          variant="dark"
          size="large"
          shape="circle"
          data-testid="action-menu-button"
          aria-label="show history"
          onClick={toggleHistory}
        >
          <LineBarsIcon />
        </IconButton>
        <IconButton
          kind="tertiary"
          variant="dark"
          size="large"
          shape="circle"
          data-testid="action-menu-button"
          aria-label="call support"
          href={`tel:${localContact.number}`}
          rel="nofollow"
          >
          <LinePhoneIcon />

        </IconButton>
      </StyledHistory>
      <StyledHeader>
        <ClickableLink
          to="/"
        >
          <StyledLELogo/>
        </ClickableLink>
      </StyledHeader>
      <StyledMenu>
        <IconButton
          kind="tertiary"
          variant="dark"
          size="large"
          shape="circle"
          data-testid="action-menu-button"
          aria-label="open new chat"
          onClick={onNewChat}
        >
          <LinePlusIcon />
        </IconButton>
        <IconButton
          kind="tertiary"
          variant="dark"
          size="large"
          shape="circle"
          data-testid="action-menu-button"
          aria-label="chat action menu"
          onClick={toggle}
          ref={triggerRef}
        >
          <LineEllipsisVIcon />
        </IconButton>
        <Dropdown
          open={open}
          triggerRef={triggerRef}
          onClose={off}
        >
          <DropdownContainer fit="dropdown-items">
            <ListItem
              disabled={disableReportFeedback}
              size="medium"
              startIcon={<LineCommentExclamationIcon />}
              title="Report a problem"
              onClick={toggleFeedback}
            />
          </DropdownContainer>
        </Dropdown>
      </StyledMenu>
    </HeaderContainer>
  )
}

export default Header
